import { useCallback, useEffect, useMemo, useState } from "react";
import useAuth from "./useAuth";
import useWebotServer from "./useWebotServer";
import { clearPersistedCache, readPersistedCache, writePersistedCache } from "../utils/persistedCache";
import type { OrgDirectoryMember, OrgDirectoryResponse } from "../@types/webotServer";

/**
 * THE colleague directory: name, avatar, email and admin-or-not for everyone in the
 * signed-in user's agency (or agencies), fetched ONCE per session and shared by every
 * screen.
 *
 * WHAT THIS REPLACES, and why one fetch matters. `FirestoreContext.organizationUsers`
 * builds the same list in the browser by running one `collectionGroup` query PER MEMBER
 * against `listingData` — 34 round trips for the largest agency — and it is wrong in two
 * ways no amount of client code can fix:
 *
 *  1. It reads `organization.users` only, while membership is `users ∪ admins`. Measured
 *     2026-08-16: THREE of 96 orgs list an admin ONLY under `admins`, so those people are
 *     missing from their own agency's list (`utils/listings/stats.ts` carries a whole
 *     helper that exists solely to paper over this).
 *  2. `firestore.rules` gates the org document on `getUserMail() in resource.data.users`,
 *     so an admins-only member cannot read their own org AT ALL and today sees an EMPTY
 *     colleague list.
 *
 * Both are resolved server-side now. `GET /api/v1/crm/org/directory` reads `dashboard-orgs`
 * as `users ∪ admins`, which is also the only source that is not client-writable.
 *
 * WHY THE CACHE IS MODULE-LEVEL RATHER THAN A REACT CONTEXT. Every consumer is a leaf —
 * a table row's owner cell, a note's author line, an option in a picker — and threading a
 * provider to all of them means editing the app shell and every tree in between. A module
 * cache with a subscriber set gives the same single-fetch guarantee with no mounting
 * order to get right, and it is the pattern already used for `services/virtualNumbers.ts`
 * and `services/signatures.ts`. It differs from those only in taking its fetcher from
 * `useWebotServer()` instead of owning an axios client, so there is exactly ONE HTTP path
 * to this endpoint and one error type (`WebotServerError`).
 *
 * THERE IS DELIBERATELY NO `orgId` PARAMETER. A directory is "who do I work with", and
 * for a multi-agency user the honest answer is all of them — the server returns the
 * deduped union. A screen that needs ONE agency's roster is asking a different question
 * and already has `crmOrgOverview(orgId)`. Keeping it unparameterized is what keeps the
 * cache single-keyed and the guarantee "one fetch per session" literally true.
 */

/**
 * Background revalidation floor.
 *
 * The roster changes ONLY when `dashboard-orgs` is edited — by support, or by an admin
 * outside this app — and there is no client write path for membership at all, so polling
 * would be pure waste. But a dashboard left open for a working day should still notice a
 * new colleague, so a consumer mounting against a cache older than this revalidates in the
 * BACKGROUND (the stale rows keep rendering; there is no spinner and no flash). Five
 * minutes is chosen to be far longer than a burst of navigation and far shorter than a
 * session.
 */
const STALE_AFTER_MS = 5 * 60 * 1000;

/**
 * ── AND IT SURVIVES A RELOAD ────────────────────────────────────────────────────
 *
 * The module cache above is worth nothing across a hard reload, which is exactly when it is
 * needed most: the CRM page cannot render its tab bar until this answers (it does not yet
 * know whether there is an agency), and the permission lookup that decides what the bar
 * SAYS cannot even start until this returns an org id. Two sequential round trips, with a
 * hole on the page for the duration.
 *
 * So the last answer is kept in `localStorage` and used as the first render's value, with
 * the fetch demoted to background revalidation. A roster changes when support edits it,
 * which is not a thing that happens between two page loads — and if it did, the correction
 * lands a moment later on its own.
 *
 * See `persistedCache.ts` for what makes that safe: stamped with the account, expiring, and
 * never written from a failure. Nothing here is an access decision — membership is resolved
 * server-side on every request.
 */
const PERSIST_KEY = "webot:orgDirectory:v1";

const EMPTY: OrgDirectoryResponse = { orgId: null, orgName: null, members: [], orgs: [] };

type Subscriber = (directory: OrgDirectoryResponse) => void;

let cache: OrgDirectoryResponse | null = null;
let cachedAt = 0;
/** The account the cache belongs to — a sign-out/sign-in must never inherit it. */
let cachedFor: string | null = null;
let inflight: Promise<OrgDirectoryResponse> | null = null;

const subscribers = new Set<Subscriber>();

const publish = (directory: OrgDirectoryResponse, forEmail: string): void => {
  cache = directory;
  cachedAt = Date.now();
  cachedFor = forEmail;
  // Only ever from a SUCCESSFUL fetch — this function is not on the failure path, which is
  // what keeps a transient 5xx from being persisted as "no agency" for the next session.
  writePersistedCache(PERSIST_KEY, forEmail, directory);
  subscribers.forEach((subscriber) => subscriber(directory));
};

/**
 * Drops the cached directory. Call after anything that could change the roster — adding
 * or removing a member of an agency — so the next consumer refetches instead of rendering
 * a colleague who has left.
 *
 * Exported for callers that are not this hook; inside it, `refresh()` is the same thing
 * plus an immediate fetch.
 */
export const invalidateOrgDirectory = (): void => {
  cache = null;
  cachedAt = 0;
  cachedFor = null;
  // ⚠️ THE PERSISTED COPY GOES TOO, AND IT HAS TO. Clearing only the memory layer would be
  // undone by the very next read: `getCachedOrgDirectory` falls back to storage and would
  // adopt the answer this call exists to discard. It also covers sign-out — the next
  // account cannot be handed the previous one's colleagues even for the frame before its
  // own request lands (the account stamp would refuse them anyway; this is the belt).
  clearPersistedCache(PERSIST_KEY);
};

/**
 * The cached directory, or null if nothing is known for this account yet.
 *
 * Falls back to the persisted copy and ADOPTS it into the module cache, so the very first
 * render after a reload has the roster and `loading` is false. `cachedAt` keeps the
 * persisted timestamp rather than now, which is what lets the staleness check below decide
 * honestly whether to revalidate.
 */
export const getCachedOrgDirectory = (email?: string | null): OrgDirectoryResponse | null => {
  const owner = (email ?? "").trim().toLowerCase();
  if (!owner) return null;
  if (cache && cachedFor === owner) return cache;

  const persisted = readPersistedCache<OrgDirectoryResponse>(PERSIST_KEY, owner);
  if (!persisted) return null;
  cache = persisted.value;
  cachedAt = persisted.at;
  cachedFor = owner;
  return cache;
};

const normalizeEmail = (value?: string | null): string => (value ?? "").trim().toLowerCase();

export type UseOrgDirectory = {
  /** Admins first, then by display name. ALWAYS includes the signed-in user. */
  members: OrgDirectoryMember[];
  /** Lowercased email → member. The per-row lookup; stable between fetches. */
  byEmail: Map<string, OrgDirectoryMember>;
  /**
   * One colleague by email, case-insensitively. Returns `null` when unknown — which is a
   * normal outcome, not an error: a contact's owner can be someone who has since left the
   * agency, and a note's author can predate the current roster. Render the email (or its
   * initials) in that case.
   */
  getMember: (email?: string | null) => OrgDirectoryMember | null;
  /** Every agency the caller belongs to. Usually one; frequently none. */
  orgs: OrgDirectoryResponse["orgs"];
  /** Set only when the answer came from exactly ONE agency; `null` across a union or with none. */
  orgId: string | null;
  orgName: string | null;
  /**
   * TRUE ONLY on the very first load, when there is nothing to render yet. A warm cache
   * reports `false` immediately even while revalidating, so navigating between screens
   * never flashes a spinner.
   */
  loading: boolean;
  /**
   * A genuine fault — network or 5xx. The endpoint does NOT refuse on scope: no agency
   * and several agencies are both 200s, so there is no permission state to classify here.
   *
   * TREAT IT AS "no names available", NOT as a screen-level failure. Every consumer
   * already has a correct fallback (the email, or initials derived from it), and blocking
   * a contact list because an avatar lookup failed would be a far worse outcome than
   * showing initials.
   */
  error: Error | null;
  /** Forces a refetch and republishes to every consumer. */
  refresh: () => Promise<void>;
};

export default function useOrgDirectory(): UseOrgDirectory {
  const { user } = useAuth();
  const { orgDirectory } = useWebotServer();

  const email = normalizeEmail(user?.email);

  const [data, setData] = useState<OrgDirectoryResponse | null>(() => getCachedOrgDirectory(email));
  const [loading, setLoading] = useState(() => getCachedOrgDirectory(email) === null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    subscribers.add(setData);
    return () => {
      subscribers.delete(setData);
    };
  }, []);

  const load = useCallback(
    async (force: boolean): Promise<void> => {
      if (!email) {
        // Signed out: publish nothing and drop whatever the previous account left behind,
        // so a sign-in as someone else cannot inherit their colleagues.
        invalidateOrgDirectory();
        setData(null);
        setLoading(false);
        return;
      }

      const warm = getCachedOrgDirectory(email);
      if (!force && warm && Date.now() - cachedAt < STALE_AFTER_MS) {
        setData(warm);
        setLoading(false);
        return;
      }

      // Concurrent mounts share one request. Without this, a page that renders the notes
      // panel and the contacts table at once fires the fetch twice on a cold start.
      if (!inflight) {
        inflight = orgDirectory()
          .then((directory) => {
            publish(directory, email);
            return directory;
          })
          .finally(() => {
            inflight = null;
          });
      }

      // ⚠️ SHOW THE STALE ANSWER FIRST, then let the request correct it. Without this line
      // a persisted-but-stale directory is held back until the fetch lands — which is the
      // whole point of persisting it, and the case is not rare: the initial state can only
      // be seeded when the account is known on the FIRST render, and Firebase auth
      // routinely resolves a tick later.
      if (warm) setData(warm);
      // The spinner is gated on having nothing to show, so a background revalidation of a
      // warm cache is invisible.
      if (!warm) setLoading(true);
      setError(null);
      try {
        await inflight;
      } catch (err) {
        console.error("useOrgDirectory: failed to load the organization directory", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    },
    [email, orgDirectory]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const refresh = useCallback(async (): Promise<void> => {
    invalidateOrgDirectory();
    await load(true);
  }, [load]);

  /**
   * The cached directory counts as the directory, even before state has caught up to it.
   *
   * `data` is seeded in a state INITIALISER, which runs once — and on a cold load the
   * account is not known on that render (auth resolves a tick later). Reading `data` alone
   * would therefore paint one frame with no colleagues and no agencies, which on the CRM
   * page means one frame with no tab bar: the flash this whole persistence layer exists to
   * remove. Derived rather than pushed into state, so the two can never disagree.
   */
  const directory = data ?? getCachedOrgDirectory(email) ?? EMPTY;

  const byEmail = useMemo(() => {
    const map = new Map<string, OrgDirectoryMember>();
    directory.members.forEach((member) => map.set(normalizeEmail(member.userId), member));
    return map;
  }, [directory]);

  const getMember = useCallback(
    (candidate?: string | null): OrgDirectoryMember | null => byEmail.get(normalizeEmail(candidate)) ?? null,
    [byEmail]
  );

  return {
    members: directory.members,
    byEmail,
    getMember,
    orgs: directory.orgs,
    orgId: directory.orgId,
    orgName: directory.orgName,
    // A cached roster on screen is not "loading" — same rule as `directory` above, and
    // consumers gate spinners on this.
    loading: loading && directory === EMPTY,
    error,
    refresh
  };
}
